import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { MeshStandardNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../../../utils/serialization/serializable.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** Noise-driven dissolve with glowing edge on `MeshStandardNodeMaterial`. */
@EngineClass('DissolveNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Dissolve Effect',
  nodeMaterialGroup: 'TSL Node FX',
})
export class DissolveNodeMaterialAsset extends NodeMaterialAsset(MeshStandardNodeMaterial) {
  readonly [ISerializableObjectTag] = true as const;

  @property({ type: 'color', description: 'Base material color' })
  override color = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'color', description: 'Edge glow color' })
    edgeColor = new THREE.Color(1.0, 0.5, 0.0);

  @property({ type: 'number', min: 0.01, max: 5.0, step: 0.01, description: 'Dissolve cycle speed' })
    speed = 0.4;

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Roughness' })
  override roughness = 0.5;

  private readonly _roughnessUniform = TSL.uniform(this.roughness);
  private readonly _speedUniform = TSL.uniform(this.speed);

  constructor() {
    super();
    this.side = THREE.DoubleSide;
    this.transparent = true;
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.speed = this.clampFiniteNumber(this.speed, 0.01, 5.0, 0.4);
    this.roughness = this.clampFiniteNumber(this.roughness, 0.0, 1.0, 0.5);
    this._roughnessUniform.value = this.roughness;
    this._speedUniform.value = this.speed;
    this.roughnessNode = this._roughnessUniform;
    this.metalnessNode = TSL.float(0.0);
    this.colorNode = null;

    const noiseVal = TSL.fract(
      TSL.positionLocal.mul(TSL.float(15)).dot(TSL.vec3(12.9898, 78.233, 45.543)).sin().mul(TSL.float(43758.5))
    );
    const threshold = TSL.oscSine(TSL.time.mul(this._speedUniform));
    const edge = TSL.smoothstep(threshold, threshold.add(TSL.float(0.08)), noiseVal);
    const edgeGlow = TSL.smoothstep(threshold.add(TSL.float(0.02)), threshold.add(TSL.float(0.08)), noiseVal).oneMinus();
    this.emissiveNode = TSL.color(this.edgeColor).mul(edgeGlow);
    this.opacityNode = edge;
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): DissolveNodeMaterialAsset {
    const instance = new DissolveNodeMaterialAsset();
    DissolveNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
